import { useState, useMemo, ReactNode } from 'react';
import { useTranslation } from 'next-i18next';
import {
  PopoverStyled, PopoverStyledButton, PopoverStyledPanel, Chip,
} from '@components/common';
import { CustomPillTopBarProps } from '@type/filters';
import { filterStyles } from '../helpers/filterStyles';


// Interface
// **********************************************
interface TopBarMixedGroupProps extends Omit<CustomPillTopBarProps, 'recordFunc' | 'idPrefix'> {
  filtersLabels: Record<string, Record<string, string> | string[] | string>,
  buttonLabel: string,
  verticalSpacingPanel?: string,
  renderPanel: () => ReactNode,
}


/**
 * @description The top bar mixed form elements group filter component. It will handle form elements with different names
 * and types. You should use it with an already built form group component.
 *
 * - **submitFunc** - the function to trigger the form submit.
 * - **clearGroup** - the function to clear the group. Defaults to undefined.
 * - **updateFormElements** - the function to force the update the form elements on open. Defaults to undefined.
 * - **updateFormValues** - the function to force the update the form values on open. Defaults to undefined.
 * - **buttonLabel** - the label for the button.
 * - **filtersLabels** - the labels for the filters.
 * - **renderPanel** - the function to render the panel.
 * - **renderFormClone** - the function to render the form clone.
 */
export const TopBarMixedGroup = (props: TopBarMixedGroupProps) => {
  // Destructure props
  const {
    submitFunc,
    clearGroup,
    updateFormElements,
    updateFormValues,
    queryParams,
    buttonLabel,
    filtersLabels,
    renderPanel,
    verticalSpacingPanel = 'py-2',
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Show/hide the component
  const [hidden, setHidden] = useState(false);

  // Get the selected labels array.
  const selectedElements = useMemo(
    () => {
      const holder: [string, string][] = [];
      Array.from(Object.entries(queryParams || {}))
        .filter((entry: [string, string | [string, string]]) => Object.keys(filtersLabels).includes(entry[0]))
        .map((entry: [string, string | [string, string]]) => {
          // make multidimensional arrays if the value is an array
          const [key, value] = entry;
          if (Array.isArray(value)) return value.map((val: string) => [key, val]);
          return entry;
        })
        .forEach((entry) => {
          // flatten the array so we can properly count and assign the labels
          if (Array.isArray(entry[1])) holder.push(...entry as [string, string][]);
          else holder.push(entry as [string, string]);
        });
      return holder;
    },
    [queryParams, filtersLabels],
  );

  // Get single selected label
  const getSingleSelectedLabel = () => {
    if (selectedElements.length === 1) {
      const [key, value] = selectedElements[0];
      // when the value is a string
      if (typeof filtersLabels[key] === 'string') return filtersLabels[key];
      // when the value is an object
      if (typeof filtersLabels[key] === 'object') {
        const label = filtersLabels[key] as Record<string, string>;
        return label[value] || '';
      }
    }
    return '';
  };

  // Handle the clear group
  const clearFilterGroup = () => {
    setHidden(true);
    if (clearGroup) clearGroup();
    if (submitFunc) setTimeout(() => submitFunc(), 50);
  };

  // Handle form elements on mount
  const handleFormElementsOnMount = () => {
    if (updateFormElements) updateFormElements();
    if (updateFormValues) updateFormValues();
  };


  // Render component
  // **********************************************
  return (
    <PopoverStyled className={`relative${hidden ? ' hidden' : ''}`}>
      {({ open }) => (
        <>
          <PopoverStyledButton
            as={Chip}
            onDelete={clearFilterGroup}
            isDropdownOpen={open}
            withDropdownArrow
            dropdownArrowPosition="left"
          >
            {
              selectedElements.length > 1
                ? `${t(buttonLabel)}: ${selectedElements.length} ${t('label.selected.multiple')}`
                : t(getSingleSelectedLabel())
            }
          </PopoverStyledButton>

          <PopoverStyledPanel
            animation="slideDown"
            anchor="bottom start"
            className={`${filterStyles.listboxOptionsWrapper} ${verticalSpacingPanel}`}
            unmount={false}
            afterEnter={handleFormElementsOnMount}
          >
            { /* form in dropdown */
              renderPanel()
            }
          </PopoverStyledPanel>

        </>
      )}
    </PopoverStyled>
  );
};
